import {
	Plugin,
	Rect, global, ResizeObserver, env
}
from 'ckeditor5';
import {
	LinesRepository
}
from './ui/lines-repository.js';

const CK_PAGINATION_ENABLED = 'ck-pagination_enabled';

export class PaginationRenderer extends Plugin {
	linesRepository;

	_scrollableEditingRootDomAncestor = null;

	_resizeObserver = null;

	_paginationLookupPlugin;

	static get pluginName() {
		return 'PaginationRenderer';
	}

	static get isOfficialPlugin() {
		return false;
	}

	static get isPremiumPlugin() {
		return false;
	}

	init() {
        
		this.set({ 'pageNumber': 1 });

        this.linesRepository = new LinesRepository(this.editor);
        this._scrollableEditingRootDomAncestor = null;
        this._resizeObserver = null;
        
        this._paginationLookupPlugin = this.editor.plugins.get('PaginationLooker');
        this.bind('isEnabled').to(this.editor.plugins.get('PaginationEditing'));
        
        this.editor.ui.once('ready', () => this._onUiReady());

        this._toggleEditingRootsClass(CK_PAGINATION_ENABLED);

        this.on('change:isEnabled', (_S, _, newValue) => {
            if(!newValue) {
                this.linesRepository.cleanLines();
            }

            this._toggleEditingRootsClass(CK_PAGINATION_ENABLED, newValue);
		});
	}

	destroy() {
		this._toggleEditingRootsClass(CK_PAGINATION_ENABLED, false);
        this.linesRepository.destroy();
        this._scrollableEditingRootDomAncestor = null;

        if (this._resizeObserver) {
            this._resizeObserver.destroy();
        }
	}

	scrollToPage(page) {
		if (!this.isEnabled) {
            return;
        }

		const top = page > 1 ? this._getPageBreakLineOffset(page - 1) : 0x0;

		this._scrollableEditingRootDomAncestor.scrollTo({
			'top': top - 10,
			'left': this._scrollableEditingRootDomAncestor.scrollLeft,
			'behavior': env.isMotionReduced ? 'instant' : 'smooth'
		});
	}

	setupScrollableAncestor() {
		const editor = this.editor;
        const attachedDomRoot = this._getAttachedDomRoot();

        if (attachedDomRoot) {
            if (this._scrollableEditingRootDomAncestor) {
                editor.ui.view.stopListening(this._scrollableEditingRootDomAncestor, 'scroll');
            }
            
            this._scrollableEditingRootDomAncestor = function(rootDomAncestor) {
                let currentElement = rootDomAncestor;
                do {
                    currentElement = currentElement.parentElement;
                    const { overflowY } = global.window.getComputedStyle(currentElement);
                    if ('auto' === overflowY || 'scroll' === overflowY) {
                        break;
                    }
                } while ('BODY' !== currentElement.tagName);
                return currentElement;
            }(attachedDomRoot);
            
            editor.ui.view.listenTo(this._scrollableEditingRootDomAncestor, 'scroll', () => {
                this._updatePageBreakLines();
            }, { 'usePassive': true });
            
            
            if(this._resizeObserver) {
                this._resizeObserver.destroy();
            }
            
            this._resizeObserver = new ResizeObserver(this._scrollableEditingRootDomAncestor, () => {
                this._updatePageBreakLines();
            });
        } else {
            editor.ui.once('update', () => this.setupScrollableAncestor());
        }
	}

	_onUiReady() {
		const editor = this.editor;

        if (this._getAttachedDomRoot()) {

            this.setupScrollableAncestor();

            editor.ui.view.listenTo(global.window, 'resize', () => this._updatePageBreakLines());

            this.listenTo(this._paginationLookupPlugin, 'change:pageBreaks', () => this._updatePageBreakLines());

        } else{
            editor.ui.once('update', () => this._onUiReady());
        }
	}

	_getAttachedDomRoot() {
		for (const viewDomRoots of this.editor.editing.view.domRoots.values()) {
			if (viewDomRoots.ownerDocument.body.contains(viewDomRoots)) {
                return viewDomRoots;
            }
        }
        
		return null;
	}

	_updatePageBreakLines() {
		if (!this.isEnabled) return;

		const attachedDomRoot = this._getAttachedDomRoot()
        const paginationPageBreaks = this._paginationLookupPlugin.pageBreaks;

        this.linesRepository.cleanLines();

		if (!attachedDomRoot || 1 === paginationPageBreaks.length) {
            this.pageNumber = 1
            return;
        }

		const domRootRect = new Rect(attachedDomRoot).excludeScrollbarsAndBorders()
        const domScrollableRect = new Rect(this._scrollableEditingRootDomAncestor).excludeScrollbarsAndBorders()
        const calculatedRectOffset = domScrollableRect.top + domScrollableRect.height / 3;

		let doPageBreak = false;
		const pageBreakLines = [];

		for (let pageNumber = 1; pageNumber < paginationPageBreaks.length; pageNumber++) {

			const pageBreakLineOffset = this._getPageBreakLineOffset(pageNumber, domScrollableRect);

			if (!pageBreakLineOffset) {
                continue;
            }

            const additionalForPageOne = pageNumber === 1 ? -205 : -305;

			const pageOffsetTop = additionalForPageOne + domScrollableRect.top + pageBreakLineOffset - this._scrollableEditingRootDomAncestor.scrollTop;

			pageBreakLines.push({
				'pageOffsetTop': pageOffsetTop,
				'domRootRect': domRootRect,
				'domScrollableRect': domScrollableRect,
				'pageNumber': pageNumber
			})

            if (pageOffsetTop > calculatedRectOffset && !doPageBreak) {
                this.pageNumber = pageNumber;
                doPageBreak = true;
            }
		}

		this._renderPageBreakLines(pageBreakLines);
        
        if (!doPageBreak) {
            this.pageNumber = paginationPageBreaks.length;
        }
	}

	_renderPageBreakLines(pageBreakLines) {
		const  { scrollX, scrollY } = global.window;

		if (pageBreakLines.length) {
			for (const { domScrollableRect, pageOffsetTop, domRootRect, pageNumber } of pageBreakLines) {
                if (domScrollableRect.top < pageOffsetTop && pageOffsetTop < domScrollableRect.bottom) {
                    this.linesRepository.showLine(
                        domRootRect.left + scrollX,
                        pageOffsetTop + scrollY,
                        domRootRect.width,
                        pageNumber + 1
                    );
                }
            }
		}
	}

	_getPageBreakLineOffset(pageNumber, domScrollableRect = null) {
		const  pageBreakLineOffset = this._paginationLookupPlugin.pageBreaks[pageNumber].offset;

        if (!pageBreakLineOffset) {
		    return 0;
        }

        if (!domScrollableRect) {
            domScrollableRect = new Rect(this._scrollableEditingRootDomAncestor).excludeScrollbarsAndBorders();
        }

        return pageBreakLineOffset - domScrollableRect.top + this._scrollableEditingRootDomAncestor.scrollTop
	}

	_toggleEditingRootsClass(className, shouldAdd = true) {
		const editorEditingView = this.editor.editing.view;

		editorEditingView.change(viewWriter => {
			for (const editingViewDocumentRoot of editorEditingView.document.roots) {
                if (shouldAdd) {
                    viewWriter.addClass(className, editingViewDocumentRoot);
                } else {
                    viewWriter.removeClass(className, editingViewDocumentRoot);
                }
            }
		});
	}
}